import React, { FC } from "react";

import { getDay, isAfter, isBefore, getDate, isToday } from "date-fns";

import { getCalendarDates, toDateString } from "~/libs";

import { HoverTooltip } from "~/components/atoms";
import { Loading } from "~/components/molecules";

import { PostsCalendarType } from "~/domains";

type PropsType = {
  isLoading?: boolean;
  dailyActivity?: PostsCalendarType[];
  targetDate: Date;
  className?: string;
  isForRate: boolean;
};

export const OriginalCalendar: FC<PropsType> = ({
  dailyActivity,
  isLoading = false,
  targetDate,
  isForRate,
  className = "",
}: PropsType) => {
  const { calendar, startMonthDate, endMonthDate } =
    getCalendarDates(targetDate);

  const calendarBgColor = (date: Date) => {
    if (isBefore(date, startMonthDate) || isAfter(date, endMonthDate))
      return "bg-secondary-100 text-secondary-400";

    const hasActivity = dailyActivity?.find(
      (v) => v.date === toDateString(date),
    );

    if (!hasActivity) return "bg-calendar-50 transition-colors";

    if (isForRate) {
      if (hasActivity.postsCount >= 100) return "bg-calendar-800 text-white";
      if (hasActivity.postsCount >= 75)
        return "bg-calendar-600 text-secondary-900";
      if (hasActivity.postsCount >= 50)
        return "bg-calendar-400 text-secondary-900";
      if (hasActivity.postsCount >= 25)
        return "bg-calendar-200 text-secondary-900";
      return "bg-calendar-50";
    } else {
      if (hasActivity.postsCount <= 0) return "bg-calendar-50";
      if (hasActivity.postsCount <= 1)
        return "bg-calendar-200 text-secondary-900";
      if (hasActivity.postsCount <= 2)
        return "bg-calendar-400 text-secondary-900";
      if (hasActivity.postsCount <= 3) return "bg-calendar-600 text-white";
      return "bg-calendar-800 text-white";
    }
  };

  const tooltipText = (date: Date) => {
    const activity = dailyActivity?.find((v) => v.date === toDateString(date));
    return `${getDate(date)}日: ${activity?.postsCount || 0}${
      isForRate ? "%" : "件"
    }`;
  };

  const dayOfWeeks = ["月", "火", "水", "木", "金", "土", "日"];

  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <Loading className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
      )}
      <div className="transition-opacity duration-300 ease-in-out">
        <table className="w-full table-auto border-separate border-spacing-1">
          <thead>
            <tr>
              {dayOfWeeks.map((dayOfWeek) => (
                <th
                  className="px-2 pb-2 text-secondary-600 font-medium text-sm"
                  key={dayOfWeek}
                >
                  {dayOfWeek}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {calendar.map((weekRow, weekIndex) => (
              <tr key={`week-${weekIndex}`}>
                {weekRow.map((date, dayIndex) => (
                  <td
                    key={`day-${getDay(date)}-${dayIndex}-${weekIndex}`}
                    className={`relative rounded-lg p-1 text-center ${calendarBgColor(date)}`}
                  >
                    <HoverTooltip
                      text={tooltipText(date)}
                      className="block h-7"
                      textClassName="text-xs min-w-[4.2rem] bg-secondary-900 text-white px-2 py-1 rounded"
                    >
                      <span className={"text-sm"}>{getDate(date)}</span>
                    </HoverTooltip>
                    {isToday(date) && (
                      <span className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-icon-heart rounded-full" />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex space-x-2 items-center justify-end mt-3 px-1">
        <span className="text-xs text-secondary-600 font-medium">少ない</span>
        <div className="flex space-x-1">
          <span className="bg-calendar-50 w-4 h-4 rounded" />
          <span className="bg-calendar-200 w-4 h-4 rounded" />
          <span className="bg-calendar-400 w-4 h-4 rounded" />
          <span className="bg-calendar-600 w-4 h-4 rounded" />
          <span className="bg-calendar-800 w-4 h-4 rounded" />
        </div>
        <span className="text-xs text-secondary-600 font-medium">多い</span>
      </div>
    </div>
  );
};
